import { useRef, useState } from "react";
import { Download, FileJson, ShieldCheck, Upload } from "lucide-react";
import type { AuthUser } from "./AuthApp";

type BackupData = Record<string, string>;

type BackupFile = {
  format: "competenceo-backup";
  version: 1;
  exportedAt: string;
  owner: { id: string; fullName: string; email?: string };
  data: BackupData;
};

function isBackupFile(value: unknown): value is BackupFile {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<BackupFile>;
  if (candidate.format !== "competenceo-backup" || candidate.version !== 1 || !candidate.data || typeof candidate.data !== "object") return false;
  return Object.entries(candidate.data).every(([key, item]) => key.startsWith("competenceo-") && typeof item === "string");
}

export function BackupView({ currentUser, disabled, onExport, onRestore }: {
  currentUser: AuthUser;
  disabled: boolean;
  onExport: () => BackupData;
  onRestore: (data: BackupData) => Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [restoring, setRestoring] = useState(false);

  function downloadBackup() {
    const payload: BackupFile = {
      format: "competenceo-backup",
      version: 1,
      exportedAt: new Date().toISOString(),
      owner: { id: currentUser.id, fullName: currentUser.fullName, email: currentUser.email },
      data: onExport(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `competenceo-sauvegarde-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setError("");
    setMessage("Sauvegarde téléchargée. Conserve ce fichier dans un endroit sûr.");
  }

  async function chooseBackup(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setMessage("");
    setError("");
    try {
      const parsed: unknown = JSON.parse(await file.text());
      if (!isBackupFile(parsed)) throw new Error("Ce fichier n’est pas une sauvegarde CompetenceO valide.");
      const date = new Date(parsed.exportedAt).toLocaleString("fr-FR");
      const confirmed = window.confirm(`Restaurer la sauvegarde du ${date} ?\n\nToutes les données actuelles de ce compte seront remplacées.`);
      if (!confirmed) return;
      setRestoring(true);
      await onRestore(parsed.data);
    } catch (reason) {
      setRestoring(false);
      setError(reason instanceof Error ? reason.message : "La restauration a échoué.");
    }
  }

  return <main className="management-page backup-page">
    <div className="management-heading"><div><span className="eyebrow">Protection des données</span><h1>Sauvegarde</h1><p>Télécharge une copie complète de ton espace professeur.</p></div></div>
    <section className="backup-grid">
      <article className="backup-card primary-backup-card">
        <div className="backup-icon"><Download size={25} /></div>
        <div><h2>Créer une sauvegarde</h2><p>Classes, élèves, salles, plans, groupes, compétences, activités, séances et évaluations.</p></div>
        <button className="primary compact" onClick={downloadBackup}><Download size={17} /> Télécharger la sauvegarde</button>
      </article>
      <article className="backup-card">
        <div className="backup-icon restore"><Upload size={25} /></div>
        <div><h2>Restaurer une sauvegarde</h2><p>Remplace les données actuelles par celles contenues dans un fichier CompetenceO.</p></div>
        <input ref={inputRef} type="file" accept="application/json,.json" hidden onChange={chooseBackup} />
        <button className="secondary backup-restore-button" disabled={disabled || restoring} onClick={() => inputRef.current?.click()}><Upload size={17} /> {restoring ? "Restauration…" : "Choisir un fichier"}</button>
        {disabled && <small>Termine la séance en cours avant de restaurer une sauvegarde.</small>}
      </article>
    </section>
    {message && <div className="backup-message success"><ShieldCheck size={18} />{message}</div>}
    {error && <div className="backup-message error"><FileJson size={18} />{error}</div>}
    <aside className="backup-warning"><ShieldCheck size={21} /><div><strong>Ce fichier ne contient aucun mot de passe.</strong><span>Il contient toutefois des données d’élèves. Ne l’envoie pas par messagerie et conserve-le dans un espace privé.</span></div></aside>
  </main>;
}
